import { useState } from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { ReviewsPortfolio } from '@/components/Portfolio/ReviewsPortfolio';
import { InstitutionalPageProvider } from '@/contexts/InstitutionalPageContext';
import MainContainer from '@/layouts/MainContainer';
import { PageLayout } from '@/layouts/PageLayout';
import { setupAPIClient } from '@/services/api';
import { ICompany, IReviews } from '@/types/api/companies';
import { ROUTER_PAGES } from '@/utils/constants';
import { AxiosError } from 'axios';

interface IReviewsProps {
  reviews: IReviews[];
  company: ICompany;
}

const Reviews = ({ reviews: initialReviews, company }: IReviewsProps) => {
  const router = useRouter();
  const [reviews, setReviews] = useState<IReviews[]>(initialReviews);

  return (
    <InstitutionalPageProvider initialCompany={company}>
      <PageLayout
        title="Avaliações"
        removeMarginTop
        onBack={() =>
          router.push(`${ROUTER_PAGES.site}/${router.query.company_slug}`)
        }
      >
        <MainContainer
          component="main"
          sx={{
            paddingTop: 6,
          }}
        >
          <ReviewsPortfolio
            reviews={reviews}
            setReviews={value => setReviews(value)}
          />
        </MainContainer>
      </PageLayout>
    </InstitutionalPageProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const redirectToHome = {
    redirect: {
      destination: '/home',
      permanent: false,
    },
  };

  try {
    const { company_slug }: { company_slug?: string } = context.query;

    if (company_slug && company_slug.match(/[A-Z]/)) {
      return {
        redirect: {
          destination: `/site/${company_slug.toLowerCase()}`,
          permanent: false,
        },
      };
    }

    const { data: company } = await setupAPIClient(context).get<ICompany>(
      `api/companies/${company_slug}/`
    );

    if (!company.has_active_institutional_page) {
      return redirectToHome;
    }

    const {
      data: { results: reviews },
    } = await setupAPIClient(context).get<{
      results: IReviews[];
    }>(`/api/companies/${company.id}/reviews/`);

    if (company_slug && reviews.length === 0) {
      return {
        redirect: {
          destination: `/site/${company_slug.toLowerCase()}`,
          permanent: false,
        },
      };
    }

    return {
      props: {
        reviews,
        company,
      },
    };
  } catch (error) {
    if ((error as AxiosError)?.response?.status === 404) {
      return {
        notFound: true,
      };
    }

    return redirectToHome;
  }
};

export default Reviews;
